import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ProjectQuery, ProjectSortOrderType } from './project.reducer';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ProjectDuplicateModalComponent } from '../../shared/dialogs/project-duplicate-modal/project-duplicate-modal.component';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { ProjectSettings } from './models/project-settings.model';
import { ProjectSummary } from './models/project-summary.model';
import { DeleteProjectModalComponent } from 'src/app/dashboard/components/delete-project-modal/delete-project-modal.component';
import { from } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { toHttpParams } from './helpers/to-http-params';
import { chunk } from 'lodash-es';
import { DialogDisplayService } from 'studio-components';
import { ProjectDeployDialogComponent } from 'src/app/shared/dialogs/project-deploy-dialog/project-deploy-dialog.component';

const PROJECT_IDS_CHUNK_SIZE = 1000;

@Injectable()
export class ProjectService {
  readonly PROJECT_SETTINGS_DIALOG_MIN_WIDTH = '400px';
  readonly PROJECT_SETTINGS_DIALOG_MAX_WIDTH = '600px';

  constructor(
    private http: HttpClient,
    public dialog: MatDialog,
    private analytics: AnalyticsService,
    private dialogDisplayService: DialogDisplayService,
  ) {}

  getProjects(ids: number[]) {
    const projectsUrl = `${environment.apiConfig.url}/admin/projects`;

    // execute requests in parallel via chunk and mergeMap
    return from(chunk(ids, PROJECT_IDS_CHUNK_SIZE)).pipe(
      map((chunkedIds) =>
        JSON.stringify({
          fields: {
            id: true,
            name: true,
            datetimeDeleted: true,
          },
          where: {
            id: { inq: chunkedIds },
          },
        }),
      ),
      mergeMap((filter) =>
        this.http.get<
          {
            id: number;
            name: string;
            datetimeDeleted: string;
          }[]
        >(projectsUrl, {
          params: {
            filter,
          },
        }),
      ),
    );
  }

  /**
   * Gets the summaries of all the projects available to the user
   */
  getProjectSummaries(sortOrder: ProjectSortOrderType, query: ProjectQuery) {
    const projectsUrl = `${environment.apiConfig.url}/project-summaries`;
    const [orderBy, sortDirection] = sortOrder.split(' ');
    const params = toHttpParams({
      orderBy,
      sortOrder: sortDirection,
      ...query,
    });

    return this.http.get<ProjectSummary[]>(projectsUrl, { params });
  }

  /**
   * Gets the project summary
   */
  getProjectSummary(projectId: number) {
    const projectsUrl = `${environment.apiConfig.url}/project-summaries/${projectId}`;
    return this.http.get<ProjectSummary>(projectsUrl);
  }

  /**
   * Gets a specific project
   */
  getProject(projectId: number) {
    const projectsUrl = `${environment.apiConfig.url}/projects/${projectId}`;
    return this.http.get(projectsUrl);
  }

  /**
   * Creates new project in the config service.
   *
   * @param projectSettings - projects settings object, not supposed to have id
   */
  createNewProject(projectSettings: ProjectSettings, previewProjectId: string | null) {
    const projectsUrl = `${environment.apiConfig.url}/projects`;
    const { name, labelId } = projectSettings;
    const data = {
      name,
      previewProjectId,
      ...(labelId ? { labelId } : null),
    };
    const project$ = this.http.post(projectsUrl, data);

    return project$;
  }

  /**
   * Duplicates a project in the config service.
   *
   * @param existingProject - projects settings object containing the existing project
   * @param projectName - The new projects name
   */
  duplicateProject(existingProject: ProjectSettings, projectName: string) {
    const projectsUrl = `${environment.apiConfig.url}/projects/${existingProject.id}/duplicate`;
    return this.http.post(projectsUrl, {
      name: projectName,
    });
  }

  /**
   * Updates the project in the config service
   *
   * @param projectSettings - project settings object, must have an id
   */
  updateProject(projectSettings: ProjectSettings, previewProjectId: string | null) {
    const projectsUrl = `${environment.apiConfig.url}/projects/${projectSettings.id}`;
    const { name, labelId } = projectSettings;
    return this.http.patch(projectsUrl, {
      name,
      labelId,
      previewProjectId,
    });
  }

  /**
   * Removes the project from the config service
   */
  deleteProject(projectId: number) {
    const projectsUrl = `${environment.apiConfig.url}/projects/${projectId}`;
    return this.http.delete(projectsUrl);
  }

  /**
   * Show the deploy project confirm dialog, and await a response
   */
  async showDeployProjectConfirm(project: ProjectSummary) {
    const dialogRef = this.dialogDisplayService.open(
      ProjectDeployDialogComponent,
      { theme: 'light' },
      project,
    );

    return await dialogRef.afterClosed().toPromise();
  }

  /**
   * Show the delete project confirm dialog, and await a response
   */
  showDeleteProjectConfirm(project: ProjectSummary) {
    const dialogRef = this.dialogDisplayService.open(
      DeleteProjectModalComponent,
      { theme: 'light' },
      project,
    );

    return dialogRef.afterClosed().pipe(
      map((confirm) => !!confirm),
      tap((confirm) => {
        this.analytics.track(
          {
            context: 'project',
            object: 'deletePrompt',
            action: confirm ? 'confirmed' : 'cancelled',
          },
          {
            projectId: project.id,
            projectName: project.name,
          },
        );
      }),
    );
  }

  /**
   * Shows the Duplicate Project component within a Material Dialog
   */
  showDuplicateProjectDialog(project: ProjectSummary) {
    const dialogRef = this.dialogDisplayService.open(
      ProjectDuplicateModalComponent,
      { theme: 'light' },
      project,
    );

    return dialogRef.afterClosed();
  }
}
